import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Eye, Phone, Star, Truck, Shield, Award } from "lucide-react";
import plantFoodSticks from "../../assets/products/block2.png";
import gardenSoilMix from "../../assets/products/25g.png";
import kgbricks from "../../assets/products/block1.png";
import growrich from "../../assets/products/650gmss.png";
import bulkload from "../../assets/cocImg/cocimg2.jpeg";
import cocoDisc from "../../assets/products/coins12.png";
import { Helmet } from "react-helmet";

const products = [
  {
    name: "Cocopeat 650GM",
    price: "₹ 120",
    originalPrice: "₹ 179",
    imgSrc: growrich,
    path: "/product/cocopeat-650gm",
    badge: "Best Seller",
    rating: 4.8,
    reviews: 124,
    description: "Premium quality cocopeat for indoor gardening and small pots"
  },
  {
    name: "Coco peat 1kg bricks",
    price: "₹ 229",
    originalPrice: "₹ 330",
    imgSrc: kgbricks,
    path: "/product/1kg-bricks",
    badge: "Popular",
    rating: 4.7,
    reviews: 89,
    description: "Compressed coco peat bricks, perfect for medium gardens"
  },
  {
    name: "Coco peat 5kg bricks",
    price: "₹ 349",
    originalPrice: "₹ 399",
    imgSrc: plantFoodSticks,
    path: "/product/5kg-bricks",
    badge: "Value Pack",
    rating: 4.9,
    reviews: 156,
    description: "Large capacity bricks for extensive gardening projects"
  },
  {
    name: "25kg Bags",
    price: "₹ 450",
    originalPrice: "₹ 539",
    imgSrc: gardenSoilMix,
    path: "/product/25kg-bricks",
    badge: "Bulk Save",
    rating: 4.6,
    reviews: 67,
    description: "Bulk packaging for commercial and large-scale use"
  },
  {
    name: "Cocopeat Coins",
    price: "₹ 450",
    originalPrice: "₹ 539",
    imgSrc: cocoDisc,
    path: "/product/cocopeat-coins",
    badge: "Easy Use",
    rating: 4.8,
    reviews: 92,
    description: "Convenient coin format for seedling and propagation"
  },
  {
    name: "Bulk Loads",
    price: "₹ 700",
    originalPrice: "₹ 859",
    imgSrc: bulkload,
    path: "/product/bulk-loads",
    badge: "Wholesale",
    rating: 4.5,
    reviews: 43,
    description: "Large quantity orders for farms and nurseries"
  },
];

const ProductGrid = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);


  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <Helmet>
        <title>Cocopeat Products | Best Cocopeat Bricks, Blocks, Discs & Bulk Loads in Andhra Pradesh</title>
        <meta name="description" content="Browse Tirumala Cocopeat's premium range of cocopeat products including bricks, blocks, discs, and bulk loads. Best quality cocopeat for agriculture, horticulture, hydroponics, and export from Anantapur, Andhra Pradesh." />
        <meta name="keywords" content="Tirumala Cocopeat, cocopeat products, cocopeat bricks, cocopeat blocks, cocopeat discs, bulk cocopeat, best cocopeat in Andhra Pradesh, best cocopeat in Anantapur, cocopeat exporters, cocopeat suppliers, cocopeat for agriculture, cocopeat for hydroponics, cocopeat for horticulture, cocopeat India, premium cocopeat, organic cocopeat, cocopeat delivery, cocopeat price, cocopeat online, cocopeat for export, cocopeat exporters, exports, best cocopeat exporters" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Cocopeat Products | Best Cocopeat Bricks, Blocks, Discs & Bulk Loads in Andhra Pradesh" />
        <meta property="og:description" content="Browse Tirumala Cocopeat's premium range of cocopeat products including bricks, blocks, discs, and bulk loads. Best quality cocopeat for agriculture, horticulture, hydroponics, and export from Anantapur, Andhra Pradesh." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tirumalacocopeat.vercel.app/products" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Cocopeat 650GM",
            image: "https://tirumalacocopeat.vercel.app/assets/products/650grampck.jpeg",
            description:
              "Premium Cocopeat 650GM - Best quality for gardening & agriculture. Buy now from Tirumala Cocopeat.",
            brand: {
              "@type": "Brand",
              name: "Tirumala Cocopeat",
            },
            offers: {
              "@type": "Offer",
              url: "https://tirumalacocopeat.vercel.app/cocopeat-650gm",
              priceCurrency: "INR",
              price: "120",
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      {/* Enhanced Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              Premium Cocopeat & Coir Products
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-green-100 mb-8 max-w-4xl mx-auto">
              India's trusted source for high-quality cocopeat and coir products. 
              Perfect for gardening, farming, and horticulture needs.
            </p>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm md:text-base">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                <span>Free Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span>Quality Assured</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>Premium Grade</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="tel:+919492228585" 
                className="inline-flex items-center bg-white text-green-700 font-semibold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-lg group"
              >
                <Phone className="mr-3 w-6 h-6 group-hover:animate-pulse" />
                Order Now: +91 94922 28585
              </a>
              <div className="text-green-100 text-center">
                <div className="text-sm">Available 24/7</div>
                <div className="text-xs">Bulk orders welcome</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Our Premium Product Range
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Choose from our carefully selected range of cocopeat products, 
            each designed for specific gardening and agricultural needs.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {products.map((product, index) => (
            <div
              key={product.name}
              className="group bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col h-full"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              {/* Product Image & Badge */}
              <div className="relative aspect-square bg-gray-50 overflow-hidden">
                {product.badge && (
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {product.badge}
                    </span>
                  </div>
                )}
                {/* <div className="absolute top-4 right-4 z-10">
                  <span className="bg-red-500 text-white px-2 py-1 rounded-md text-xs font-bold">
                    {calculateDiscount(product.originalPrice, product.price)}% OFF
                  </span>
                </div> */}
                <img
                  src={product.imgSrc}
                  alt={`${product.name} - Premium quality cocopeat`}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Product Info */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    {product.name}
                  </h3>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(product.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {product.rating} ({product.reviews} reviews)
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Price */}
                  {/* <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl font-bold text-emerald-600">
                      {product.price}
                    </span>
                    <span className="text-lg text-gray-400 line-through">
                      {product.originalPrice}
                    </span>
                  </div> */}
                </div>

                {/* Contact Info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-600 mb-1">For Orders & Queries:</p>
                  <a 
                    href="tel:+919492228585" 
                    className="text-emerald-600 font-semibold hover:underline text-sm"
                  >
                    +91 94922 28585
                  </a>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Link 
                    to={product.path} 
                    className="flex-1"
                    aria-label={`View details for ${product.name}`}
                  >
                    <button className="w-full flex items-center justify-center bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium border border-gray-200">
                      <Eye className="mr-2 w-5 h-5" />
                      View Details
                    </button>
                  </Link>
                  
                  <a 
                    href="tel:+919492228585" 
                    className="flex-1"
                    aria-label={`Call to order ${product.name}`}
                  >
                    <button className="w-full flex items-center justify-center bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 transition-colors duration-300 font-medium shadow-sm">
                      <Phone className="mr-2 w-5 h-5" />
                      Call Now
                    </button>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Footer */}
        <div className="mt-16 bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl p-8 md:p-12 text-white text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Need Help Choosing the Right Product?
          </h3>
          <p className="text-lg mb-6 text-emerald-100">
            Our experts are here to help you select the perfect cocopeat solution for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="tel:+919492228585" 
              className="inline-flex items-center bg-white text-emerald-600 font-semibold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-lg"
            >
              <Phone className="mr-3 w-6 h-6" />
              Call +91 94922 28585
            </a>
            <div className="text-emerald-100 text-center">
              <div className="text-sm">Expert consultation available</div>
              <div className="text-xs">Bulk pricing on request</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductGrid;